import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Globe } from 'lucide-react';

import { SearchableSelect } from '@/components/ui/searchable-select';
import { accountTimeZoneService } from '@/services/accountTimeZoneService';
import { timeZoneOptions } from '@/lib/timeZones';
import { supabase } from '@/lib/supabaseClient';

interface AccountTimeZoneSettingProps {
  tenantId: string;
}

// The account time zone, beside the pause toggle because automations are what read it: a task's
// "due today" or "due in 3 days" is the end of that day here. Admins change it; everyone else sees it.
export function AccountTimeZoneSetting({ tenantId }: AccountTimeZoneSettingProps) {
  const [timeZone, setTimeZone] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const options = useMemo(() => timeZoneOptions(), []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([supabase.rpc('is_admin_or_superadmin'), accountTimeZoneService.get(tenantId)])
      .then(([{ data: adminCheck }, zone]) => {
        if (cancelled) return;
        setIsAdmin(!!adminCheck);
        setTimeZone(zone);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) toast.error('Failed to load the account time zone');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleChange = async (next: string) => {
    if (!next || next === timeZone) return;
    const previous = timeZone;
    setTimeZone(next);
    try {
      setSaving(true);
      setTimeZone(await accountTimeZoneService.set(tenantId, next));
      toast.success(`Account time zone set to ${next}`);
    } catch (error) {
      setTimeZone(previous);
      toast.error(error instanceof Error ? error.message : 'Failed to change the account time zone');
    } finally {
      setSaving(false);
    }
  };

  const label = options.find((o) => o.value === timeZone)?.label ?? timeZone ?? '';

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-muted/30 px-4 py-3">
      <div className="flex items-start gap-3">
        <Globe className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div>
          <div className="text-sm font-medium">Account time zone</div>
          <div className="text-xs text-muted-foreground">
            Automations count days in this time zone: a task due today is due by the end of today here.
          </div>
        </div>
      </div>
      {isAdmin ? (
        <SearchableSelect
          className="w-72"
          options={options}
          value={timeZone ?? undefined}
          onValueChange={handleChange}
          placeholder="Select a time zone"
          disabled={timeZone === null || saving}
        />
      ) : (
        <div className="text-sm">{label}</div>
      )}
    </div>
  );
}
